import { DateForPmis } from '.';
import { DayJsWrapper } from './dayjs-wrapper';
import { DateTimeFormat } from './datetime-format.enum';

export class DateTimeHelper {
    DatesAreEqual(date1: Date, date2: Date) {
        return (
            date1 &&
            date2 &&
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    }

    GetDateOnly(dt: Date): DateForPmis {
        return DateForPmis.Create(
            dt.getFullYear(),
            dt.getMonth(),
            dt.getDate()
        );
    }

    GetDateTimeString(date: Date | undefined, format = ''): string {
        if (!date) {
            return '';
        }
        return DayJsWrapper.format(
            date,
            format || DateTimeFormat.dateTimeFormat
        );
    }

    GetDateString(date: Date | undefined): string {
        return this.GetDateTimeString(date, DateTimeFormat.dateFormat);
    }
    GetTimeString(date: Date | undefined): string {
        return this.GetDateTimeString(date, DateTimeFormat.timeFormat);
    }
    GetDateTimeStringWithSecond(date: Date): string {
        return this.GetDateTimeString(
            date,
            `${DateTimeFormat.dateTimeFormat}:ss`
        );
    }

    private _cnt = 0;
    CheckStringIsUtcDate(str: string): Date | undefined {
        let dts = str || '';
        if (dts.startsWith('0001-01-01T00:00:00')) {
            return new DateForPmis('2001-01-01T00:00:00');
        }
        if (parseInt(dts.substring(0, 4)) < 1800) {
            return undefined;
        }

        dts += dts.endsWith('Z') ? '' : 'Z';
        const dt = new Date(dts);
        if (dt && !isNaN(dt.valueOf()) && dt.getFullYear() > 1800) {
            const ds = DayJsWrapper.utcString(dt);
            if (
                ds.length > 16 &&
                ds.substring(0, 16) === dts.substring(0, 16)
            ) {
                this._cnt++;
                if (this._cnt % 5000 === 0)
                    console.log('checkStringIsUtcDate', this._cnt);
                const dt2 = new DateForPmis(dt);
                dt2.tryConvertToSystem();
                return dt2;
            }
        }
        return undefined;
    }

    CreateTimeline(
        rangeStart: Date,
        rangeEnd: Date,
        interval: string
    ): TimelineDate[] {
        const units = new Map<string, number>([
            ['hh', 60],
            ['mm', 1],
            ['dd', 24 * 60],
        ]);

        const match = interval.match(/^(\d+)(hh|mm|dd)$/);
        let num = 60;
        let multiplier = 1;
        if (match) {
            const [, numStr, unit] = match;
            multiplier = units.get(unit) || 1;
            num = parseInt(numStr, 10);
        }

        const stepMinutes = num * multiplier;
        const timeline: TimelineDate[] = [];
        let previousDate: Date | null = null;
        for (
            let currentDate = new Date(rangeStart);
            currentDate.getTime() <= rangeEnd.getTime();
            currentDate = new Date(currentDate.getTime() + stepMinutes * 60000)
        ) {
            const tld = {
                date: new Date(currentDate),
                displayFormat: 'HH:mm',
                cssClass: 'font-smaller-2',
                type: '',
            };
            if (
                !previousDate ||
                currentDate.getDate() !== previousDate.getDate()
            ) {
                previousDate = new Date(currentDate);
                tld.displayFormat = 'MMM dd';
                tld.cssClass = 'font-weight-bold font-smaller-1';
                // tld.type = 'sticky';
            }
            timeline.push(tld);
        }

        return timeline;
    }

    AddDates<T extends Date>(date: T, days: number): DateForPmis {
        const dt = new DateForPmis(date);
        dt.setDate(dt.getDate() + days);
        return dt;
    }

    AddMinutes<T extends Date>(date: T, minutes: number): DateForPmis {
        const dt = new DateForPmis(date);
        dt.setMinutes(dt.getMinutes() + minutes);
        return dt;
    }

    DurationString(minutes: number): string {
        const mins = minutes % 60;
        const minsStr = mins > 0 ? ` ${mins} minute${mins > 1 ? 's' : ''}` : '';
        if (minutes < 60) {
            if (minutes === 0) {
                return '';
            }
            return `${minsStr}`;
        }

        const hours = Math.floor(minutes / 60);
        const hoursStr = ` hour${hours % 24 > 1 ? 's' : ''} ${minsStr}`;
        if (hours < 24) {
            return `${hours}${hoursStr}`;
        }

        const days = Math.floor(hours / 24);
        const hrs = hours % 24;
        return `${days} day${days > 1 ? 's' : ''} ${hrs}${hoursStr}`;
    }

    DurationStringShort(minutes: number): string {
        if (minutes <= 0) return '';

        const mins = minutes % 60;
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        const hrs = hours % 24;

        const parts: string[] = [];

        if (days > 0) {
            parts.push(`${days} d`);
        }
        if (hrs > 0) {
            parts.push(`${hrs} hr${hrs > 1 ? 's' : ''}`);
        }
        if (mins > 0) {
            parts.push(`${mins} min${mins > 1 ? 's' : ''}`);
        }

        return parts.join(' ');
    }

    DurationStringShortest(minutes: number): string {
        if (minutes <= 0) return '';

        const mins = minutes % 60;
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        const hrs = hours % 24;

        const parts: string[] = [];

        if (days > 0) {
            parts.push(`${days} d`);
        }
        if (hrs > 0) {
            parts.push(`${hrs} h`);
        }
        if (mins > 0) {
            parts.push(`${mins} m`);
        }

        return parts.join(' ');
    }
}

export interface TimelineDate {
    date: Date;
    displayFormat: string;
    cssClass: string;
}
